import React, { useEffect, useRef } from 'react';
import { NavHashLink as NavLink } from 'react-router-hash-link';
import { FaLinkedinIn, FaGithub, FaEnvelope } from 'react-icons/fa';
import { gsap } from '../../lib/gsap';
import { useLenis } from '../SmoothScroll/SmoothScroll';
import { headerData } from '../../data/headerData';
import { contactsData } from '../../data/contactsData';
import './Landing.css';

function Landing() {
  const lenis = useLenis();
  const heroRef = useRef(null);
  const plateRef = useRef(null);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 900px)').matches;
    const heroEl = heroRef.current;
    const plateEl = plateRef.current;
    if (!heroEl) return undefined;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.from('.hero-glow', { opacity: 0, scale: 0.8, duration: 1.4 })
        .from('.hero-name', { opacity: 0, y: 44, duration: 0.9 }, '-=1.0')
        .from('.hero-title', { opacity: 0, y: 24, duration: 0.7 }, '-=0.6')
        .from('.hero-lead', { opacity: 0, y: 24, duration: 0.7 }, '-=0.5')
        .from('.hero-actions', { opacity: 0, y: 24, duration: 0.7 }, '-=0.5')
        .from('.portrait-plate', { opacity: 0, scale: 0.92, y: 30, duration: 1 }, '-=0.8')
        .from('.social-chip', { opacity: 0, y: 15, stagger: 0.08, duration: 0.5 }, '-=0.5')
        .from('.scroll-indicator', { opacity: 0, duration: 0.8 }, '-=0.2');

      if (!isMobile) {
        gsap.to('.hero-grid', {
          y: 80,
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: heroEl,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      }
    }, heroEl);

    const handleMouseMove = (e) => {
      if (isMobile || !plateEl) return;
      const rect = plateEl.getBoundingClientRect();
      const x = e.clientX - (rect.left + rect.width / 2);
      const y = e.clientY - (rect.top + rect.height / 2);

      gsap.to(plateEl, {
        rotateY: (x / rect.width) * 14,
        rotateX: -(y / rect.height) * 14,
        duration: 0.5,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      if (!plateEl) return;
      gsap.to(plateEl, {
        rotateY: 0,
        rotateX: 0,
        duration: 0.8,
        ease: 'power2.out',
      });
    };

    if (plateEl) {
      plateEl.addEventListener('mousemove', handleMouseMove);
      plateEl.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (plateEl) {
        plateEl.removeEventListener('mousemove', handleMouseMove);
        plateEl.removeEventListener('mouseleave', handleMouseLeave);
      }
      ctx.revert();
    };
  }, []);

  const scrollToSection = (el) => {
    if (!lenis) return;
    lenis.scrollTo(el, {
      offset: -80,
      duration: 1.4,
      easing: (t) => 1 - Math.pow(1 - t, 4),
    });
  };

  return (
    <section className="hero" id="landing" ref={heroRef}>
      <div className="hero-glow" aria-hidden="true" />

      <div className="hero-grid">
        <div className="hero-copy">
          <h1 className="hero-name">
            {headerData.name.split(' ')[0]}
            <br />
            {headerData.name.split(' ')[1]}
          </h1>

          <p className="hero-title">{headerData.title}</p>
          <p className="hero-lead">{headerData.desciption}</p>

          <div className="hero-actions">
            <a
              href={headerData.resumePdf}
              target="_blank"
              rel="noreferrer"
              className="cta-pill"
            >
              <span>Download resume</span>
              <div className="icon-wrapper" aria-hidden="true">↗</div>
            </a>
            <NavLink
              to="/#projects"
              smooth
              scroll={scrollToSection}
              className="cta-secondary"
            >
              View work →
            </NavLink>
          </div>
        </div>

        <div className="hero-visual">
          <div className="portrait-plate" ref={plateRef}>
            <img
              src={headerData.image}
              alt={headerData.name}
              className="portrait-img"
            />
            <div className="portrait-glare" aria-hidden="true" />
          </div>

          <div className="hero-socials">
            <a href={contactsData.linkedIn} target="_blank" rel="noreferrer" className="social-chip" aria-label="LinkedIn">
              <FaLinkedinIn />
            </a>
            <a href={contactsData.github} target="_blank" rel="noreferrer" className="social-chip" aria-label="GitHub">
              <FaGithub />
            </a>
            <a href={`mailto:${contactsData.email}`} className="social-chip" aria-label="Email">
              <FaEnvelope />
            </a>
          </div>
        </div>
      </div>

      <div className="scroll-indicator" aria-hidden="true">
        <span className="scroll-text">scroll to explore</span>
        <span className="scroll-chevron" />
      </div>
    </section>
  );
}

export default Landing;
